import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Shield, Plus } from "lucide-react";
import PermissionMatrix from "./PermissionMatrix";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/enterprise`;

const CreateRoleDialog = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    role_scope: "subsidiary",
    level: 3,
    permissions: []
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name) {
      toast.error("Please enter a role name");
      return;
    }
    
    if (formData.permissions.length === 0) {
      toast.error("Please select at least one permission");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/roles`, {
        ...formData,
        is_system_role: false // Custom roles created by company
      });
      
      toast.success("Role created successfully");
      
      // Reset form
      setFormData({
        name: "",
        description: "",
        role_scope: "subsidiary",
        level: 3,
        permissions: []
      });
      
      if (onSuccess) onSuccess(response.data);
      onClose();
    } catch (error) {
      console.error("Error creating role:", error);
      toast.error(error.response?.data?.detail || "Failed to create role");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Create Custom Role
          </DialogTitle>
          <DialogDescription>
            Create a custom role with specific permissions for your company
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Basic Information */}
          <div>
            <h3 className="font-medium text-sm mb-3 pb-2 border-b">Basic Information</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Role Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Regional Director"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder="Oversee multiple subsidiaries within a geographic region"
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="role_scope">Role Scope</Label>
                  <Select
                    value={formData.role_scope}
                    onValueChange={(value) => handleChange("role_scope", value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="company">Company-wide</SelectItem>
                      <SelectItem value="subsidiary">Subsidiary-specific</SelectItem>
                      <SelectItem value="multi_subsidiary">Multi-Subsidiary</SelectItem>
                      <SelectItem value="department">Department-specific</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    Defines the scope where this role applies
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="level">Hierarchy Level</Label>
                  <Select
                    value={formData.level.toString()}
                    onValueChange={(value) => handleChange("level", parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Level 1 (Highest - Company Admin)</SelectItem>
                      <SelectItem value="2">Level 2 (Subsidiary Manager)</SelectItem>
                      <SelectItem value="3">Level 3 (Team Lead)</SelectItem>
                      <SelectItem value="4">Level 4 (Staff)</SelectItem>
                      <SelectItem value="5">Level 5 (Viewer)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    Lower number = higher authority
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Permissions */}
          <div>
            <h3 className="font-medium text-sm mb-3 pb-2 border-b flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Permissions Configuration
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              Select the permissions this role will have. Use quick templates or select individually.
            </p>
            <PermissionMatrix
              selectedPermissions={formData.permissions}
              onChange={(permissions) => handleChange("permissions", permissions)}
            />
          </div>

          {/* Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-sm mb-2 text-blue-900">Role Summary</h4>
            <div className="space-y-1 text-sm text-blue-800">
              <p><strong>Name:</strong> {formData.name || "Not set"}</p>
              <p><strong>Scope:</strong> {formData.role_scope}</p>
              <p><strong>Level:</strong> {formData.level}</p>
              <p><strong>Permissions:</strong> {formData.permissions.length} selected</p>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Creating..." : "Create Role"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateRoleDialog;
